import type { PositionArchitecture, PositionBootstrap } from '../data-contract/position-management';

export const selectArchitecture = (
    architectures: readonly PositionArchitecture[],
    requestedId: string | null,
): PositionArchitecture | undefined =>
    architectures.find((architecture) => architecture.id === requestedId) ??
    architectures.find(({ is_default }) => is_default) ??
    architectures[0];

export const withAddedArchitecture =
    (architecture: PositionArchitecture) =>
    (bootstrap: PositionBootstrap | undefined): PositionBootstrap | undefined =>
        bootstrap === undefined || bootstrap.architectures.some((candidate) => candidate.id === architecture.id)
            ? bootstrap
            : { ...bootstrap, architectures: [...bootstrap.architectures, architecture] };

const withEachArchitecture =
    (update: (candidate: PositionArchitecture) => PositionArchitecture) =>
    (bootstrap: PositionBootstrap | undefined): PositionBootstrap | undefined =>
        bootstrap === undefined ? bootstrap : { ...bootstrap, architectures: bootstrap.architectures.map(update) };

export const withConnectedArchitecture = (architecture: PositionArchitecture) =>
    withEachArchitecture((candidate) => (candidate.id === architecture.id ? architecture : candidate));

export const withManagersCanEdit = (architecture: PositionArchitecture) =>
    withEachArchitecture((candidate) =>
        candidate.id === architecture.id
            ? { ...candidate, managers_can_edit: architecture.managers_can_edit }
            : candidate,
    );

export const withDefaultArchitecture = (architecture: PositionArchitecture) =>
    withEachArchitecture((candidate) => ({ ...candidate, is_default: candidate.id === architecture.id }));

export const mustSwitchDefaultFirst = (
    architecture: PositionArchitecture,
    architectures: readonly PositionArchitecture[],
): boolean => architecture.is_default && architectures.some(({ id }) => id !== architecture.id);

export const withoutArchitecture =
    (architectureId: string) =>
    (bootstrap: PositionBootstrap | undefined): PositionBootstrap | undefined =>
        bootstrap === undefined
            ? bootstrap
            : {
                  ...bootstrap,
                  architectures: bootstrap.architectures.filter((architecture) => architecture.id !== architectureId),
              };
